import { v4 as uuidv4 } from "uuid";
import { replaceToken } from "./utils";

(async (Drupal, drupalSettings) => {
  const baseRuleIdKey = drupalSettings.analytics_tokens?.view_rule_id;
  const personToken = await drupalSettings.analytics_tokens.personTokenPromise;
  let cacheKey = null;

  /**
   * Set the view rule ID in browser storage
   * @param {string} ruleId The value to save to storage
   */
  function saveToStorage(ruleId) {
    try {
      if (!cacheKey) {
        throw Error("cacheKey is null");
      }

      sessionStorage.setItem(cacheKey, ruleId);
    } catch (e) {
      console.warn("Could not save view_rule_id to storage", e);
    }
  }

  /**
   * Replace person token tokens with the real value within drupalSettings object.
   * @param {string} ruleId The view rule id value
   * @param {object} settings DrupalSettings object
   */
  function replaceInDrupalSettings(ruleId, settings = drupalSettings) {
    replaceToken(settings, "[analytics_token:view_rule_id]", ruleId);
  }

  /**
   * Call the API for authenticated user to get a rule ID.
   * @param {string} ruleIdKey The rule ID key.
   * @throws Will throw an error if api returns an invalid response.
   * @returns {string} View rule ID.
   */
  async function getViewRuleIdFromApi(ruleIdKey) {
    const response = await fetch(`/api/view-rule/${ruleIdKey}-${personToken}`);

    if (!response.ok) {
      throw new Error(
        `Failed to load view_rule_id: ${response.status} ${response.statusText}`,
      );
    }

    const apiData = await response.json();

    const viewRuleId = apiData.view_rule_id;

    saveToStorage(viewRuleId);

    replaceInDrupalSettings(viewRuleId);

    return viewRuleId;
  }

  /**
   * Main logic to obtain a rule ID.
   * @param {string} ruleIdKey The rule ID key.
   * @throws Will throw an error if api returns an invalid response.
   * @returns {string} View rule ID.
   */
  function getVideoViewRuleId(ruleIdKey) {
    // Storage key always based on baseRuleId + personToken (or anon)
    cacheKey = `analytics_tokens-view_rule_id:${ruleIdKey}:${personToken || "anon"}`;

    // 🔹 Try to reuse cached value
    let cachedValue = null;
    try {
      cachedValue = sessionStorage.getItem(cacheKey);
    } catch (e) {
      console.warn("Could not access sessionStorage", e);
    }

    if (cachedValue) {
      console.info("Using cached view_rule_id:", cachedValue);

      replaceInDrupalSettings(cachedValue);

      return cachedValue;
    }

    // If there is no person token → generate UUID fallback and cache it
    if (!personToken) {
      console.info(
        "No person token available, generating UUID for view_rule_id.",
      );
      let fallbackId = uuidv4();

      saveToStorage(fallbackId);

      replaceInDrupalSettings(fallbackId);

      return fallbackId;
    }

    // Otherwise → fetch from API
    return getViewRuleIdFromApi(ruleIdKey);
  }

  /**
   * Create a promise to get the view rule ID.
   * @param {string} ruleIdKey The rule ID key.
   * @returns {Promise} Promise for the rule ID.
   */
  function getPromiseForVideoViewRuleId(ruleIdKey) {
    return new Promise((resolve, reject) => {
      try {
        const viewRuleId = getVideoViewRuleId(ruleIdKey);

        resolve(viewRuleId);
      } catch (err) {
        console.error("Video view_rule_id fetch error:", err);

        reject(err);
      }
    });
  }

  if (baseRuleIdKey) {
    drupalSettings.analytics_tokens.videoViewRuleIdPromise =
      getPromiseForVideoViewRuleId(baseRuleIdKey);
  } else {
    console.warn("No base view_rule_id in drupalSettings on page load.");

    drupalSettings.analytics_tokens.videoViewRuleIdPromise = null;
  }

  Drupal.behaviors.replaceViewRuleId = {
    attach: async (context, settings) => {
      if (
        !settings.analytics_tokens.videoViewRuleIdPromise &&
        settings.analytics_tokens.view_rule_id
      ) {
        settings.analytics_tokens.videoViewRuleIdPromise =
          getPromiseForVideoViewRuleId(settings.analytics_tokens.view_rule_id);
      }

      const ruleId = await settings.analytics_tokens.videoViewRuleIdPromise;

      replaceInDrupalSettings(ruleId, settings);
    },
  };

  /*
  window.addEventListener("AnalyticsTokenUpdate", async (data) => {
    if (data.detail.key !== "view_rule_id") {
      return;
    }

    if (cacheKey) {
      sessionStorage.removeItem(cacheKey);
    }

    drupalSettings.analytics_tokens.videoViewRuleIdPromise =
      getPromiseForVideoViewRuleId(
        drupalSettings.analytics_tokens.view_rule_id,
      );
  });
*/
})(Drupal, drupalSettings);
